import { Notification, MessageBox } from 'element-ui'

// 新版本检测：发版后提醒还开着旧页面的人刷新。
//
// 做法是轮询 index.html，比对里面入口资源的文件名。构建产物带内容 hash
// （/js/app.af6f8dd0.js），改了代码 hash 就变，所以不需要额外生成版本号文件、
// 也不用改构建配置。nginx 已经给 index.html 配了 no-cache，拿到的一定是最新的。
//
// 只提醒、不强制刷新：编辑器里可能有没保存的内容，替用户做主刷新会直接弄丢。
// 用户点了确认才刷新，而 UnifiedEditor 注册的 beforeunload 会再拦一道。

// 只看入口 chunk。路由懒加载的分包会在运行时陆续插进 DOM，
// 拿它们当基准会随用户点了哪些页面而变，比不出东西来。
const ENTRY_ASSET = /\/(?:js|css)\/(?:app|chunk-vendors)\.[a-z0-9]+\.(?:js|css)/gi

function normalize(paths: string[]): string {
  return Array.from(new Set(paths.map(p => p.toLowerCase()))).sort().join('|')
}

/** 当前页面正在跑的版本：从已加载的 DOM 里取，而不是再请求一次 index.html。
 *  请求来的永远是最新版，拿它当基准的话，「加载页面后随即发版」这种情况就永远发现不了。 */
function currentVersion(): string {
  const urls: string[] = []
  document.querySelectorAll<HTMLScriptElement>('script[src]')
    .forEach(s => urls.push(s.src))
  document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"][href]')
    .forEach(l => urls.push(l.href))
  return normalize(urls.join(' ').match(ENTRY_ASSET) || [])
}

/** 线上最新版本；取不到返回空串（发布中、断网等），由调用方当作「未知」跳过本次 */
async function latestVersion(): Promise<string> {
  // 加时间戳是双保险：即使哪天 nginx 的 no-cache 掉了，也不会拿到浏览器缓存
  const res = await fetch(`/index.html?_t=${Date.now()}`, { cache: 'no-store' })
  if (!res.ok) return ''
  const html = await res.text()
  return normalize(html.match(ENTRY_ASSET) || [])
}

let started = false

export interface VersionCheckOptions {
  /** 轮询间隔（毫秒），默认 5 分钟 */
  intervalMs?: number
}

export function startVersionCheck(opts: VersionCheckOptions = {}): void {
  // 开发环境资源不带 hash，而且 HMR 本来就会自动更新，没有检查的必要
  if (process.env.NODE_ENV !== 'production') return
  if (started) return
  started = true

  const intervalMs = opts.intervalMs ?? 5 * 60 * 1000
  const baseline = currentVersion()
  // 认不出入口资源（构建方式变了、或被套了一层壳）时不启用，
  // 免得基准是空串、跟线上任何值都不等，一上来就误报
  if (!baseline) return

  let notified = false
  let timer: number | undefined

  const stop = () => {
    if (timer !== undefined) {
      window.clearInterval(timer)
      timer = undefined
    }
    document.removeEventListener('visibilitychange', onVisible)
  }

  const notify = () => {
    if (notified) return
    notified = true
    stop() // 已经提醒过了，再轮询没有意义

    Notification({
      title: '有新版本',
      message: '同事发布了新版本，你当前用的还是旧页面。点这条提示刷新；如果有正在编辑、未保存的内容，请先保存再刷新。',
      type: 'warning',
      duration: 0, // 不自动消失：一闪而过等于没提醒
      showClose: true,
      onClick: () => {
        MessageBox.confirm(
          '刷新会重新加载页面，未保存的编辑内容将丢失。确定现在刷新吗？',
          '刷新到新版本',
          { confirmButtonText: '刷新', cancelButtonText: '稍后再说', type: 'warning' }
        )
          .then(() => window.location.reload())
          .catch(() => {
            // 选了「稍后再说」：把提示重新挂出来，否则这条消失后就再也不提醒了
            notified = false
            notify()
          })
      },
    })
  }

  const check = async () => {
    try {
      const latest = await latestVersion()
      // 取不到就跳过：发布过程中 index.html 可能短暂读不到，
      // 这时候不该当成「版本变了」去打扰用户
      if (latest && latest !== baseline) notify()
    } catch {
      // 断网/超时，下次再说
    }
  }

  timer = window.setInterval(check, intervalMs)

  // 切回标签页时立刻查一次。大部分人是开着页面去干别的，
  // 回来时正好是该提醒的时机，光靠定时器可能要再等几分钟
  function onVisible() {
    if (document.visibilityState === 'visible') check()
  }
  document.addEventListener('visibilitychange', onVisible)

  check()
}
